






































































import { computed, defineComponent, ref } from "@vue/composition-api";
import { FileId } from "@scrile/api-provider/dist/api/FileProvider";
import { InputParentalConsentCreate } from "@scrile/api-provider/dist/api/ParentalConsentProvider";
import { UploadProgress } from "@scrile/tools/dist/lib/FileUploader";
import useAuthUser from "@/hooks/useAuthUser";
import useFiles from "@/hooks/useFiles";
import store from "@/store";
import { MUTATION_TYPES } from "@/store/const";
import providers from "@/lib/api";
import { $t } from "@/lib/i18nHelpers";
import { FormValues } from "@/types";
import AvatarDescription from "@/components/AvatarDescription";
import BaseButton from "@/components/BaseButton";
import BaseCheckbox from "@/components/BaseCheckbox";
import BaseConfirm from "@/components/BaseConfirm";
import BaseForm from "@/components/BaseForm";
import BasePage from "@/components/BasePage";
import PayeeInfoInputFile from "@/views/PagePayeeInfo/components/PayeeInfoInputFile.vue";

export default defineComponent({
  name: "PageParentalConsent",
  components: { BaseButton, PayeeInfoInputFile, BaseForm, BaseCheckbox, AvatarDescription, BaseConfirm, BasePage },
  setup(_, { root }) {
    const { authUser } = useAuthUser();
    const { uploadThenUpdate } = useFiles();
    const agreement = ref(false);

    const isValid = ref(false);
    const progress = ref<Record<string, { progress: number; isLoading: boolean }>>({
      parental_id_photo: {
        progress: 100,
        isLoading: false,
      },
      parental_photo_with_id: {
        progress: 100,
        isLoading: false,
      },
    });

    const values = ref<FormValues>({
      name: null,
      email: null,
      parental_id_photo: {
        data: null,
      },
      parental_photo_with_id: {
        data: null,
      },
    });

    const nameTrimmed = computed(() => values.value.name && values.value.name.trim());

    const fields = computed(() => {
      return [
        {
          name: "name",
          label: $t("Name of parent"),
          rules: ["required"],
          validationMode: "aggressive",
        },
        {
          name: "email",
          label: $t("Email of parent"),
          type: "email",
          rules: ["required"],
          validationMode: "aggressive",
        },
        {
          name: "photo_container",
          type: "photoContainer",
          value: [
            {
              name: "parental_id_photo",
              type: "inputFile",
              label: $t("Photo or scan of parent’s ID"),
              meta: {
                required: true,
              },
            },
            {
              name: "parental_photo_with_id",
              type: "inputFile",
              label: $t("Photo of parent holding their ID"),
              meta: {
                required: true,
              },
            },
          ],
        },
      ];
    });

    const getFiles = async (type: string) => {
      if (!authUser.value) return;
      const response = await providers.FileProvider.find({
        fileIds: [{ subjectId: authUser.value.id, type }],
      });
      values.value[type].data = response.find(file => file.fileId.type === type) ?? null;
    };

    const checkValid = () => {
      isValid.value =
        values.value.name &&
        values.value.email &&
        values.value.parental_id_photo.data &&
        values.value.parental_photo_with_id.data &&
        agreement.value;
    };

    const upload = async (file: FileList, type: string) => {
      if (!authUser.value) return;
      const fileId: FileId = {
        subjectId: authUser.value.id,
        type,
        fileNumber: 0,
      };
      const onUploadProgress = (uploadProgress: UploadProgress) => {
        const res = ((uploadProgress?.loaded ?? 0) / (uploadProgress?.total ?? 0)) * 100;
        progress.value[type].progress = res === 100 ? 99.9 : res;
      };
      progress.value[type].progress = 0;
      progress.value[type].isLoading = true;

      const onSuccessCb = () => {
        progress.value[type].progress = 100;
        progress.value[type].isLoading = false;
      };
      await uploadThenUpdate({ fileId, file: file[0], onUploadProgress, onSuccessCb });
      await getFiles(type);
      checkValid();
    };

    const handleDelete = (type: string) => {
      values.value[type].data = null;
    };

    const onSubmit = async () => {
      if (!authUser.value || !isValid.value) return;
      const data: InputParentalConsentCreate = {
        userId: authUser.value.id,
        name: values.value.name.trim(),
        email: values.value.email.trim(),
      };
      const response = await providers.ParentalConsentProvider.create({ data });
      store.commit(MUTATION_TYPES.SET_PARENTAL_CONSENT, response);
      root.$router.replace({ name: "profilePayeeInfo" });
    };

    return {
      isValid,
      authUser,
      agreement,
      values,
      fields,
      progress,
      nameTrimmed,
      checkValid,
      upload,
      handleDelete,
      onSubmit,
    };
  },
});
